import React from 'react';
import {
    Box,
    TextField,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    Avatar,
    Stack,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const logoColors = {
    primaryBlue: '#3f51b5',
    brightYellow: '#c4d835',
    darkBlue: '#2c387e',
    lightYellow: '#d4e157',
};

// One reusable field style so the 12 name/phone inputs below don't each carry their own
// 40-line sx block - matches the look of the rest of BookingForm without the repetition.
const fieldSx = (dense = false) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: `${logoColors.lightYellow}08`,
        borderRadius: '10px',
        color: logoColors.brightYellow,
        fontWeight: 500,
        ...(dense && { fontSize: '0.9rem' }),
        '& fieldset': { borderColor: `${logoColors.lightYellow}50`, borderWidth: '1px' },
        '&:hover fieldset': { borderColor: logoColors.lightYellow },
        '&.Mui-focused fieldset': { borderColor: logoColors.brightYellow, borderWidth: '2px' },
        '&.Mui-error fieldset': { borderColor: '#ff4444' },
        '& input': { color: logoColors.brightYellow, fontWeight: 500 },
    },
    '& .MuiInputLabel-root': {
        color: `${logoColors.brightYellow}90`,
        '&.Mui-focused': { color: logoColors.brightYellow },
        '&.MuiInputLabel-shrink': { color: logoColors.brightYellow },
        '&.Mui-error': { color: '#ff4444' },
    },
    '& .MuiFormHelperText-root': { color: '#ff4444', fontSize: '0.7rem' },
});

const SHARED_SLOTS = 6;

export const emptyParticipants = () =>
    Array.from({ length: SHARED_SLOTS }, () => ({ name: '', phone: '', amountPaid: 0 }));

// Runs the same "name always required, phone required only for slot 0" rule the backend
// enforces, so the user sees the problem before submitting instead of after a 400 comes back.
export const validateParticipants = (participants) => {
    const errors = [];
    (participants || []).forEach((p, i) => {
        if (!p.name || !p.name.trim()) errors.push(`Person ${i + 1}: name is required`);
        if (i === 0 && (!p.phone || !p.phone.trim())) errors.push('Person 1: phone number is required');
    });
    return errors;
};

/**
 * Individual/Shared toggle + (when Shared) the 6 fixed participant slots and guest count.
 * Fully controlled - the parent (BookingForm / edit dialog) owns isShared/additionalGuestsCount/
 * participants and passes them down, so this component has no state of its own.
 */
const SharedBookingSection = ({
    isShared,
    onToggleShared,
    additionalGuestsCount,
    onGuestsChange,
    participants,
    onParticipantsChange,
    totalAmount,
    disabled = false,
}) => {
    const splitCount = Math.max(SHARED_SLOTS + (additionalGuestsCount || 0), 1);
    const perPerson = totalAmount ? Math.round((totalAmount / splitCount) * 100) / 100 : 0;

    const updateParticipant = (index, field, value) => {
        const next = [...participants];
        next[index] = { ...next[index], [field]: value };
        onParticipantsChange(next);
    };

    return (
        <Box sx={{ gridColumn: '1 / -1' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: isShared ? 3 : 0 }}>
                <ToggleButtonGroup
                    exclusive
                    value={isShared ? 'shared' : 'individual'}
                    onChange={(_, val) => val && onToggleShared(val === 'shared')}
                    disabled={disabled}
                    sx={{
                        borderRadius: '14px',
                        overflow: 'hidden',
                        border: `1px solid ${logoColors.lightYellow}50`,
                        '& .MuiToggleButton-root': {
                            color: `${logoColors.brightYellow}90`,
                            border: 'none',
                            px: 3,
                            py: 1,
                            fontWeight: 600,
                            textTransform: 'none',
                            gap: 1,
                            '&.Mui-selected': {
                                background: `linear-gradient(45deg, ${logoColors.brightYellow} 0%, ${logoColors.lightYellow} 100%)`,
                                color: logoColors.darkBlue,
                                '&:hover': { background: logoColors.lightYellow },
                            },
                        },
                    }}
                >
                    <ToggleButton value="individual"><PersonIcon fontSize="small" /> Individual</ToggleButton>
                    <ToggleButton value="shared"><GroupsIcon fontSize="small" /> Shared</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {isShared && (
                <Box
                    sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: '16px',
                        border: `1px solid ${logoColors.lightYellow}30`,
                        background: 'rgba(255,255,255,0.03)',
                    }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
                        <Typography sx={{ color: logoColors.brightYellow, fontWeight: 600 }}>
                            Splitting between {splitCount} {splitCount === 1 ? 'person' : 'people'}
                        </Typography>
                        <Typography sx={{ color: logoColors.lightYellow, fontWeight: 700 }}>
                            Rs {perPerson.toLocaleString()} / person
                        </Typography>
                    </Box>

                    <Stack spacing={1.5}>
                        {participants.map((p, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                <Avatar
                                    sx={{
                                        width: 32, height: 32, fontSize: '0.85rem', fontWeight: 700,
                                        bgcolor: `${logoColors.brightYellow}25`, color: logoColors.brightYellow,
                                        flexShrink: 0,
                                    }}
                                >
                                    {i + 1}
                                </Avatar>
                                <TextField
                                    size="small"
                                    label={`Name`}
                                    value={p.name}
                                    onChange={(e) => updateParticipant(i, 'name', e.target.value)}
                                    disabled={disabled}
                                    required
                                    sx={{ ...fieldSx(true), flex: '1 1 45%', minWidth: 140 }}
                                />
                                <TextField
                                    size="small"
                                    label={i === 0 ? 'Phone (required)' : 'Phone (optional)'}
                                    value={p.phone}
                                    onChange={(e) => updateParticipant(i, 'phone', e.target.value)}
                                    disabled={disabled}
                                    required={i === 0}
                                    placeholder="+92 3XX XXXXXXX"
                                    sx={{ ...fieldSx(true), flex: '1 1 45%', minWidth: 160 }}
                                />
                            </Box>
                        ))}
                    </Stack>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2.5 }}>
                        <Typography sx={{ color: `${logoColors.brightYellow}90`, fontSize: '0.9rem' }}>
                            Additional guests (not individually tracked)
                        </Typography>
                        <IconButton
                            size="small"
                            disabled={disabled || !additionalGuestsCount}
                            onClick={() => onGuestsChange(Math.max((additionalGuestsCount || 0) - 1, 0))}
                            sx={{ color: logoColors.brightYellow, border: `1px solid ${logoColors.lightYellow}40` }}
                        >
                            <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ color: logoColors.lightYellow, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>
                            {additionalGuestsCount || 0}
                        </Typography>
                        <IconButton
                            size="small"
                            disabled={disabled}
                            onClick={() => onGuestsChange((additionalGuestsCount || 0) + 1)}
                            sx={{ color: logoColors.brightYellow, border: `1px solid ${logoColors.lightYellow}40` }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default SharedBookingSection;
